import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Mail, Play, Loader2, RefreshCw, ChevronDown, ChevronRight,
  CheckCircle2, XCircle, AlertTriangle, Paperclip, MessageSquare,
} from "lucide-react";

const TYPE_LABELS = {
  hotel: "🏨 Hotel", transfer: "🚐 Transfer", activity: "🎟️ Actividad",
  restaurant: "🍽️ Restaurante", train: "🚆 Tren", flight: "✈️ Vuelo",
  rental_car: "🚗 Coche", other: "📌 Otros",
};

const PHASE_COLORS = {
  solicitud: "bg-blue-100 text-blue-800",
  disponibilidad: "bg-cyan-100 text-cyan-800",
  confirmacion: "bg-emerald-100 text-emerald-800",
  reconfirmacion: "bg-emerald-100 text-emerald-800",
  peticion_datos: "bg-amber-100 text-amber-800",
  envio_datos: "bg-indigo-100 text-indigo-800",
  precio: "bg-purple-100 text-purple-800",
  pago: "bg-pink-100 text-pink-800",
  factura: "bg-pink-100 text-pink-800",
  voucher: "bg-teal-100 text-teal-800",
  modificacion: "bg-orange-100 text-orange-800",
  cancelacion: "bg-red-100 text-red-800",
  recordatorio: "bg-gray-200 text-gray-700",
  otro: "bg-gray-100 text-gray-600",
};

const fmtDate = (iso) => (iso ? iso.slice(0, 10) : "—");

export default function ConversationsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [runStatus, setRunStatus] = useState({ status: "idle" });
  const [polling, setPolling] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/learning/conversations");
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al cargar conversaciones");
    } finally {
      setLoading(false);
    }
  };

  const fetchStatus = async () => {
    try {
      const { data: st } = await api.get("/learning/conversations/status");
      setRunStatus(st);
      if (st.status !== "running") {
        setPolling(false);
        if (st.status === "done") {
          toast.success("Análisis de conversaciones completado");
          fetchData();
        } else if (st.status === "error") {
          toast.error(`Análisis falló: ${st.error}`);
        }
      }
    } catch (e) { /* polling reintenta */ }
  };

  useEffect(() => {
    fetchData();
    api.get("/learning/conversations/status").then(({ data: st }) => {
      setRunStatus(st);
      if (st.status === "running") setPolling(true);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!polling) return undefined;
    const id = setInterval(fetchStatus, 5000);
    return () => clearInterval(id);
  }, [polling]);

  const startRun = async () => {
    if (!window.confirm(
      "Se leerá el buzón de heidi@viajadverdad.com (SOLO LECTURA, no se envía ni se marca nada) buscando las conversaciones de los 6 trips con proveedores MAIL de los últimos 6 meses.\n\nTarda ~10-15 minutos. ¿Continuar?"
    )) return;
    try {
      await api.post("/learning/conversations/run", {});
      toast.info("Análisis iniciado — leyendo buzón de Heidi…");
      setRunStatus({ status: "running" });
      setPolling(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo iniciar");
    }
  };

  const isEmpty = !data || data.empty;
  const convs = data?.conversations || [];
  const byTrip = {};
  convs.forEach((c) => { (byTrip[c.trip_external_id] = byTrip[c.trip_external_id] || []).push(c); });

  return (
    <div className="space-y-5" data-testid="conversations-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-500 max-w-2xl">
          Leemos (solo lectura) el buzón <b>heidi@viajadverdad.com</b>, localizamos las conversaciones
          completas con los proveedores que se reservan por <b>MAIL</b> en los 6 trips de muestra, y la IA
          aprende cómo funciona la relación de principio a fin: solicitud → confirmación → datos → voucher.
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchData} disabled={loading} data-testid="conv-refresh">
            <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> Refrescar
          </Button>
          <Button onClick={startRun} disabled={runStatus.status === "running"}
            className="bg-purple-600 hover:bg-purple-700" data-testid="conv-run">
            {runStatus.status === "running"
              ? <><Loader2 size={14} className="mr-1 animate-spin" /> Analizando…</>
              : <><Play size={14} className="mr-1" /> Analizar buzón de Heidi</>}
          </Button>
        </div>
      </div>

      {runStatus.status === "running" && (
        <div className="vv-card p-4 border-l-4 border-purple-500 bg-purple-50/50 flex items-center gap-3" data-testid="conv-status-banner">
          <Loader2 size={20} className="animate-spin text-purple-600" />
          <div>
            <div className="font-medium text-purple-900">Leyendo buzón y analizando conversaciones…</div>
            <div className="text-xs text-purple-700">{runStatus.progress || "Iniciando…"}</div>
          </div>
        </div>
      )}

      {isEmpty ? (
        <div className="vv-card p-12 text-center text-gray-500">
          <Mail size={48} className="mx-auto mb-3 text-gray-300" />
          <p>Aún no se ha analizado el buzón. Pulsa <b>&quot;Analizar buzón de Heidi&quot;</b>.</p>
          <p className="text-xs mt-2 text-gray-400">Solo lectura — no se envía ningún correo ni se marca nada como leído.</p>
        </div>
      ) : (
        <>
          <div className="vv-card p-4 text-sm flex flex-wrap gap-6" data-testid="conv-stats">
            <Stat label="Trips" value={data?.stats?.trips_processed} />
            <Stat label="Proveedores MAIL" value={data?.stats?.mail_providers_found} />
            <Stat label="Conversaciones" value={data?.stats?.conversations_found} />
            <Stat label="Mensajes leídos" value={data?.stats?.messages_read} />
            <Stat label="Analizadas con IA" value={data?.stats?.llm_analyzed} />
            <Stat label="Snapshot" value={data?.created_at?.slice(0, 16).replace("T", " ")} />
          </div>

          {data?.playbook && <Playbook playbook={data.playbook} />}

          {data?.trips?.length > 0 && <TripsOverview trips={data.trips} />}

          {Object.entries(byTrip).map(([tid, list]) => (
            <div key={tid} className="space-y-2">
              <h2 className="text-sm font-semibold text-gray-700 mt-2">
                Trip {tid} — {list[0]?.client_search || list[0]?.client_name}
                <span className="text-gray-400 font-normal ml-2">{list.length} conversación{list.length !== 1 ? "es" : ""}</span>
              </h2>
              {list.map((c) => <ConversationCard key={c.id} conv={c} />)}
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function Playbook({ playbook }) {
  const [open, setOpen] = useState(true);
  const types = Object.entries(playbook?.por_tipo || {});
  return (
    <div className="vv-card border-2 border-purple-200" data-testid="conv-playbook">
      <button className="w-full p-4 flex items-center justify-between" onClick={() => setOpen(!open)} data-testid="conv-playbook-toggle">
        <div className="flex items-center gap-2 font-semibold text-purple-900">
          <MessageSquare size={18} /> Playbook aprendido — cómo funcionan las conversaciones
        </div>
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-4">
          {playbook.general?.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">Patrones globales</div>
              <ul className="space-y-1">
                {playbook.general.map((g, i) => (
                  <li key={i} className="text-xs text-gray-700 flex gap-2"><span className="text-purple-400">•</span>{g}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {types.map(([t, pb]) => (
              <div key={t} className="border border-gray-200 rounded-lg p-3 bg-gray-50/50" data-testid={`conv-playbook-${t}`}>
                <div className="font-medium text-sm mb-2">{TYPE_LABELS[t] || t}</div>
                <PlaybookSection title="Flujo típico" items={pb.flujo_tipico} ordered />
                <PlaybookSection title="Señales de confirmación" items={pb.senales_confirmacion} />
                <PlaybookSection title="Datos que devuelve" items={pb.datos_que_devuelve} />
                <PlaybookSection title="Riesgos" items={pb.riesgos} warn />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PlaybookSection({ title, items, ordered, warn }) {
  if (!items?.length) return null;
  return (
    <div className="mb-2">
      <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">{title}</div>
      <ul className="space-y-0.5">
        {items.map((it, i) => (
          <li key={i} className={`text-[11px] flex gap-1.5 ${warn ? "text-amber-800" : "text-gray-700"}`}>
            <span className="shrink-0 text-gray-400">{ordered ? `${i + 1}.` : "•"}</span>{it}
          </li>
        ))}
      </ul>
    </div>
  );
}

function TripsOverview({ trips }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="vv-card">
      <button className="w-full p-4 flex items-center justify-between" onClick={() => setOpen(!open)} data-testid="conv-trips-toggle">
        <div className="font-semibold text-sm">Proveedores detectados por trip (MAIL vs descartados)</div>
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          {trips.map((t) => (
            <div key={t.trip_id} className="text-xs border-t border-gray-100 pt-2">
              <div className="font-medium mb-1">Trip {t.trip_id} — {t.client_search || t.client_name} <span className="text-gray-400">({t.n_bookings} reservas)</span></div>
              {t.error ? <div className="text-red-600">Error: {t.error}</div> : (
                <div className="flex flex-wrap gap-1.5">
                  {(t.mail_providers || []).map((p) => (
                    <span key={p.name} className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded px-2 py-0.5">
                      ✉️ {p.name} <span className="text-emerald-500">×{p.n_threads} hilos</span>
                    </span>
                  ))}
                  {(t.skipped_providers || []).map((p, i) => (
                    <span key={i} className="bg-gray-50 border border-gray-200 text-gray-400 rounded px-2 py-0.5" title={p.motivo}>
                      {p.name} ({p.motivo})
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ConversationCard({ conv }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [showMsgs, setShowMsgs] = useState(false);
  const a = conv.analysis || {};
  const confirmed = a.confirmacion_final === true;
  const hasError = !!a.error;

  const loadMessages = async () => {
    if (detail) { setShowMsgs(!showMsgs); return; }
    try {
      const { data } = await api.get(`/learning/conversations/${conv.id}`);
      setDetail(data);
      setShowMsgs(true);
    } catch (err) {
      toast.error("No se pudieron cargar los mensajes");
    }
  };

  return (
    <div className="vv-card" data-testid={`conv-card-${conv.id}`}>
      <button className="w-full p-3 flex items-start gap-3 text-left" onClick={() => setOpen(!open)}>
        <div className="mt-0.5">{open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-sm">{conv.provider_name}</span>
            {(conv.service_types || []).map((t) => (
              <span key={t} className="text-[10px] bg-gray-100 rounded px-1.5 py-0.5">{TYPE_LABELS[t] || t}</span>
            ))}
            <span className="text-[10px] text-gray-400">{conv.n_messages} msgs · {fmtDate(conv.first_date)} → {fmtDate(conv.last_date)}</span>
            {conv.match_confidence === "media" && (
              <span className="text-[10px] bg-amber-50 border border-amber-200 text-amber-700 rounded px-1.5 py-0.5">match por proveedor</span>
            )}
          </div>
          {a.resumen && <p className="text-xs text-gray-600 mt-1 line-clamp-2">{a.resumen}</p>}
          {hasError && <p className="text-xs text-red-500 mt-1">IA: {a.error}</p>}
        </div>
        <div className="shrink-0">
          {hasError ? null : confirmed ? (
            <span className="inline-flex items-center gap-1 text-[11px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">
              <CheckCircle2 size={11} /> Confirmada
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[11px] bg-gray-50 text-gray-500 border border-gray-200 rounded-full px-2 py-0.5">
              <XCircle size={11} /> Sin confirmar
            </span>
          )}
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 ml-6 space-y-3">
          {a.discrepancias?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs text-amber-900" data-testid={`conv-discrepancias-${conv.id}`}>
              <div className="flex items-center gap-1.5 font-medium mb-1"><AlertTriangle size={12} /> Discrepancias detectadas</div>
              <ul className="space-y-0.5 ml-4 list-disc">{a.discrepancias.map((d, i) => <li key={i}>{d}</li>)}</ul>
            </div>
          )}

          {a.datos_confirmados && Object.values(a.datos_confirmados).some((v) => v && (typeof v !== "object" || Object.keys(v).length)) && (
            <div className="flex flex-wrap gap-2 text-[11px]">
              {Object.entries(a.datos_confirmados).map(([k, v]) => {
                if (!v || (typeof v === "object" && !Object.keys(v).length)) return null;
                const val = typeof v === "object" ? JSON.stringify(v) : String(v);
                return (
                  <span key={k} className={`border rounded px-2 py-0.5 ${k === "tipo_habitacion" ? "bg-amber-50 border-amber-300 text-amber-900 font-medium" : "bg-blue-50 border-blue-200 text-blue-800"}`}>
                    {k.replace(/_/g, " ")}: <b>{val.slice(0, 60)}</b>
                  </span>
                );
              })}
            </div>
          )}

          {a.fases?.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">Flujo de la conversación</div>
              <div className="space-y-1">
                {a.fases.map((f) => (
                  <div key={f.n} className="flex items-start gap-2 text-xs">
                    <span className="shrink-0 w-5 text-gray-400 text-right">{f.n}.</span>
                    <span className={`shrink-0 w-20 text-[10px] uppercase tracking-wide ${f.de === "agencia" ? "text-blue-600" : "text-gray-600"}`}>{f.de}</span>
                    <span className={`shrink-0 text-[10px] rounded px-1.5 py-0.5 ${PHASE_COLORS[f.tipo] || PHASE_COLORS.otro}`}>{f.tipo}</span>
                    <span className="text-gray-700">{f.resumen}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {a.patrones?.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">Patrones de este proveedor</div>
              <ul className="space-y-0.5">
                {a.patrones.map((p, i) => <li key={i} className="text-xs text-gray-700 flex gap-1.5"><span className="text-purple-400">•</span>{p}</li>)}
              </ul>
            </div>
          )}

          <Button variant="outline" size="sm" onClick={loadMessages} className="h-7 text-xs" data-testid={`conv-messages-btn-${conv.id}`}>
            <Mail size={12} className="mr-1" />
            {showMsgs ? "Ocultar mensajes" : `Ver mensajes (${conv.n_messages})`}
          </Button>

          {showMsgs && detail?.messages?.map((m, i) => (
            <div key={i} className={`rounded-lg border p-2.5 text-xs ${m.direction === "agencia" ? "bg-blue-50/50 border-blue-200 ml-8" : "bg-gray-50 border-gray-200 mr-8"}`}>
              <div className="flex flex-wrap items-center gap-2 text-[10px] text-gray-500 mb-1">
                <span className={`font-medium uppercase ${m.direction === "agencia" ? "text-blue-700" : "text-gray-700"}`}>{m.direction}</span>
                <span>{m.date?.slice(0, 16).replace("T", " ")}</span>
                <span className="truncate">{m.from}</span>
              </div>
              <div className="font-medium text-gray-800 mb-1">{m.subject}</div>
              <pre className="whitespace-pre-wrap font-sans text-gray-700 max-h-56 overflow-y-auto">{m.body || "(sin texto)"}</pre>
              {m.attachments?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {m.attachments.map((att, j) => (
                    <span key={j} className="inline-flex items-center gap-1 bg-white border border-gray-200 rounded px-1.5 py-0.5 text-[10px] text-gray-600">
                      <Paperclip size={9} /> {att}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className="text-lg font-semibold mt-0.5">{value ?? "—"}</div>
    </div>
  );
}
